import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight, Star, Menu, X, ChevronLeft, ChevronRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import BeforeAfterSlider from "@/components/site/BeforeAfterSlider";
import ReviewText from "@/components/site/ReviewText";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const ARCHIVO = { fontFamily: "'Archivo Black', system-ui, sans-serif" };
const MANROPE = { fontFamily: "'Manrope', system-ui, sans-serif" };

const DEFAULT_GALLERY = [
  "https://images.pexels.com/photos/13141770/pexels-photo-13141770.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.pexels.com/photos/1669754/pexels-photo-1669754.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  "https://images.pexels.com/photos/12998745/pexels-photo-12998745.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720",
];

const SERVICE_FALLBACK_IMAGES = [
  "https://images.pexels.com/photos/13141770/pexels-photo-13141770.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  "https://images.pexels.com/photos/1669754/pexels-photo-1669754.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  "https://images.pexels.com/photos/12998745/pexels-photo-12998745.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720",
  "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.pexels.com/photos/9301939/pexels-photo-9301939.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  "https://images.pexels.com/photos/8092529/pexels-photo-8092529.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
];

const FALLBACK_REVIEWS = [
  { id: "r1", author: "Familie Becker", rating: 5, text: "Absolut sauber gearbeitet, pünktlich und freundlich. Unsere Wohnung sieht aus wie neu!", date: "März 2025" },
  { id: "r2", author: "Markus L.", rating: 5, text: "Fachlich top, ein echter Meister seines Fachs. Die Beratung war ehrlich und das Ergebnis übertrifft unsere Erwartungen.", date: "Februar 2025" },
  { id: "r3", author: "Sabine H.", rating: 5, text: "Vom ersten Kontakt bis zur Endabnahme: höflich, professionell, präzise.", date: "Januar 2025" },
];

export default function HomeBold({ siteInfo, gallery, beforeAfter, reviews, services }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const serviceList = (services && services.length > 0)
    ? services.map((s, i) => ({
        title: s.title,
        text: s.text,
        image: s.image_path ? fileUrl(s.image_path) : SERVICE_FALLBACK_IMAGES[i % SERVICE_FALLBACK_IMAGES.length],
      }))
    : SERVICE_FALLBACK_IMAGES.map((image, i) => ({ title: ["Innenanstrich","Fassadenarbeiten","Tapezieren","Spachteltechniken","Bodenbeläge","Renovierung"][i], text: "", image }));

  const navLinks = [
    { href: "#leistungen", label: "Leistungen" },
    { href: "#arbeit", label: "Arbeit" },
    { href: "#vorher-nachher", label: "Vorher/Nachher" },
    { href: "#stimmen", label: "Stimmen" },
    { href: "#kontakt", label: "Kontakt" },
  ];

  const heroImages = gallery.length > 0
    ? gallery.slice(0, 4).map((g) => fileUrl(g.image_path))
    : DEFAULT_GALLERY;

  const galleryItems = gallery.length > 0
    ? gallery.map((g, i) => ({ id: g.id, src: fileUrl(g.image_path), title: g.title || "", description: g.description || "" }))
    : DEFAULT_GALLERY.map((src, i) => ({ id: `d${i}`, src, title: "" }));

  const galleryRef = useRef(null);
  const scrollGallery = (dir) => {
    const el = galleryRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.75, behavior: "smooth" });
  };

  const baList = beforeAfter && beforeAfter.length > 0
    ? beforeAfter.map((it) => ({ id: it.id, title: it.title, beforeSrc: fileUrl(it.before_path), afterSrc: fileUrl(it.after_path) }))
    : [{ id: "demo", title: "Wohnzimmer Renovation",
        beforeSrc: "https://images.unsplash.com/photo-1503594384566-461fe158e797?auto=format&fit=crop&w=1600&q=80",
        afterSrc: "https://images.pexels.com/photos/13141770/pexels-photo-13141770.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200" }];

  const reviewList = reviews && reviews.length > 0 ? reviews : FALLBACK_REVIEWS;
  const computedAvg = reviewList.length
    ? (reviewList.reduce((a, r) => a + (r.rating || 5), 0) / reviewList.length).toFixed(1)
    : "5.0";
  const ratingText = siteInfo?.google_rating || computedAvg;
  const totalCount = siteInfo?.google_reviews_count > 0 ? siteInfo.google_reviews_count : reviewList.length;
  const [showAllReviews, setShowAllReviews] = useState(false);
  const visibleReviews = showAllReviews ? reviewList : reviewList.slice(0, 6);

  const handleContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Vielen Dank! Wir melden uns in Kürze.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Senden.");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="home-bold" style={{ ...MANROPE, backgroundColor: "#0A0A0A", color: "#F4F0E8" }} className="min-h-screen">
      {/* NAVBAR */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-[#2A2A2A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3" data-testid="bold-navbar-logo">
            {siteInfo?.logo_path ? (
              <img src={fileUrl(siteInfo.logo_path)} alt="Logo" className="h-10 w-auto object-contain" />
            ) : (
              <span style={ARCHIVO} className="text-xl tracking-tight text-[#F4F0E8]">{siteInfo?.company_name?.toUpperCase() || "MALERMEISTER"}</span>
            )}
          </a>
          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} className="text-sm text-[#A8A39A] hover:text-[#D4A949] transition-colors duration-200 uppercase tracking-wider">
                {l.label}
              </a>
            ))}
            <a href="#kontakt" className="px-6 py-2.5 bg-[#D4A949] text-[#0A0A0A] text-sm uppercase tracking-wider hover:bg-[#E5C063] transition-colors font-semibold">
              Anfrage stellen
            </a>
          </nav>
          <button onClick={() => setOpen(!open)} className="lg:hidden text-[#F4F0E8]" aria-label="Menü">
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden bg-[#0A0A0A] border-t border-[#2A2A2A]">
            <nav className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-[#F4F0E8] uppercase tracking-wider text-sm py-1">{l.label}</a>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* HERO — Full-bleed photo with mega headline */}
      <section id="top" className="relative h-screen min-h-[700px] overflow-hidden">
        <img src={siteInfo?.hero_image_path ? fileUrl(siteInfo.hero_image_path) : heroImages[0]} alt="Malerarbeit" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/40 via-[#0A0A0A]/30 to-[#0A0A0A]" />
        <div className="relative h-full max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-20 lg:pb-32">
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <p className="text-[#D4A949] text-xs uppercase tracking-[0.3em] mb-6 font-semibold">{siteInfo?.address?.split(",").slice(-1)[0]?.trim() || "Stuttgart"} · Meisterbetrieb · {siteInfo?.years_experience || 25}+ Jahre</p>
            <h1 style={ARCHIVO} className="text-[clamp(3rem,9vw,9rem)] leading-[0.85] tracking-[-0.04em] text-[#F4F0E8] uppercase mb-8" data-testid="bold-hero-headline">
              Farbe.<br />Form.<br /><span className="text-[#D4A949]">Charakter.</span>
            </h1>
            <p className="text-lg sm:text-xl text-[#A8A39A] max-w-2xl mb-10 leading-relaxed">
              {siteInfo?.tagline}. {siteInfo?.description?.split(".")[0]}.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#kontakt" className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4A949] text-[#0A0A0A] uppercase tracking-wider font-semibold hover:bg-[#E5C063] transition-colors">
                Projekt anfragen <ArrowUpRight className="w-4 h-4" />
              </a>
              <a href="#arbeit" className="inline-flex items-center gap-2 px-8 py-4 border border-[#F4F0E8] text-[#F4F0E8] uppercase tracking-wider hover:bg-[#F4F0E8] hover:text-[#0A0A0A] transition-colors">
                Referenzen ansehen
              </a>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-8 right-8 hidden lg:flex items-center gap-3 text-[#A8A39A] text-xs uppercase tracking-[0.3em]">
          <span className="w-12 h-px bg-[#D4A949]"></span>
          Scroll
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-[#0A0A0A] border-y border-[#2A2A2A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { value: `${siteInfo?.years_experience || 25}+`, label: "Jahre Erfahrung" },
            { value: `${siteInfo?.projects_completed || 480}+`, label: "Projekte realisiert" },
            { value: siteInfo?.google_rating || "4.9", label: "Google-Bewertung" },
            { value: siteInfo?.stat4_value || "100%", label: siteInfo?.stat4_label || "Meisterbetrieb" },
          ].map((s, i) => (
            <div key={i} className="text-center lg:text-left">
              <p style={ARCHIVO} className="text-5xl lg:text-6xl text-[#D4A949] mb-2 leading-none">{s.value}</p>
              <p className="text-xs text-[#A8A39A] uppercase tracking-[0.2em]">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* LEISTUNGEN — Big image-card grid */}
      <section id="leistungen" className="py-24 lg:py-32 bg-[#0A0A0A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-6">
            <div>
              <p className="text-[#D4A949] text-xs uppercase tracking-[0.3em] font-semibold mb-4">01 — Leistungen</p>
              <h2 style={ARCHIVO} className="text-5xl lg:text-7xl leading-[0.95] text-[#F4F0E8] uppercase tracking-[-0.02em]">
                Was wir<br /><span className="text-[#D4A949]">können</span>
              </h2>
            </div>
            <p className="text-[#A8A39A] max-w-md text-lg leading-relaxed">
              Vom feinen Innenanstrich bis zur kompletten Renovierung – ein Ansprechpartner für alle Gewerke.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
            {serviceList.map((s, i) => (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
                className="group relative aspect-[4/5] overflow-hidden cursor-pointer"
                data-testid={`bold-service-${i}`}
              >
                <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.5s] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  <p className="text-[#D4A949] text-xs uppercase tracking-[0.3em] mb-3 font-semibold">{String(i + 1).padStart(2, "0")}</p>
                  <h3 style={ARCHIVO} className="text-3xl text-[#F4F0E8] uppercase mb-3 tracking-tight">{s.title}</h3>
                  <p className="text-[#C7C2B9] text-sm leading-relaxed max-w-xs opacity-0 group-hover:opacity-100 transition-opacity duration-500">{s.text}</p>
                </div>
                <div className="absolute top-6 right-6 w-10 h-10 border border-[#F4F0E8]/30 rounded-full flex items-center justify-center group-hover:bg-[#D4A949] group-hover:border-[#D4A949] transition-colors">
                  <ArrowUpRight className="w-4 h-4 text-[#F4F0E8] group-hover:text-[#0A0A0A]" />
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ARBEIT / GALLERY — Marquee photo grid */}
      <section id="arbeit" className="py-24 lg:py-32 bg-[#0F0F0F]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <p className="text-[#D4A949] text-xs uppercase tracking-[0.3em] font-semibold mb-4">02 — Referenzen</p>
              <h2 style={ARCHIVO} className="text-5xl lg:text-7xl leading-[0.95] text-[#F4F0E8] uppercase tracking-[-0.02em]">
                Unsere<br />Arbeit
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-10">
              <p className="text-lg text-[#A8A39A] leading-relaxed">{siteInfo?.description}</p>
            </div>
          </div>

          {/* Two-row horizontal scroll gallery */}
          <div className="relative" data-testid="bold-gallery-scroller">
            <div className="flex justify-end gap-3 mb-6">
              <button
                type="button"
                onClick={() => scrollGallery(-1)}
                aria-label="Galerie nach links scrollen"
                data-testid="bold-gallery-prev"
                className="w-12 h-12 flex items-center justify-center border border-[#2A2A2A] text-[#F4F0E8] hover:border-[#D4A949] hover:text-[#D4A949] transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => scrollGallery(1)}
                aria-label="Galerie nach rechts scrollen"
                data-testid="bold-gallery-next"
                className="w-12 h-12 flex items-center justify-center border border-[#2A2A2A] text-[#F4F0E8] hover:border-[#D4A949] hover:text-[#D4A949] transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <div ref={galleryRef} className="overflow-x-auto scrollbar-hide snap-x snap-mandatory">
              <div
                className="grid grid-rows-2 grid-flow-col gap-3 lg:gap-4"
                style={{ gridAutoColumns: "min(78vw, 460px)" }}
              >
                {galleryItems.map((it, i) => (
                  <div key={it.id} data-testid={`bold-gallery-item-${i}`} className="relative overflow-hidden group snap-start h-[220px] sm:h-[260px] lg:h-[320px]">
                    <img
                      src={it.src}
                      alt={it.title || `Projekt ${i + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                    />
                    {it.title && (
                      <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                        <p className="text-[#F4F0E8] text-sm font-semibold uppercase tracking-wider">{it.title}</p>
                        {it.description && <p className="text-[#A8A39A] text-xs mt-0.5">{it.description}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VORHER/NACHHER */}
      <section id="vorher-nachher" className="py-24 lg:py-32 bg-[#0A0A0A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-6">
            <div>
              <p className="text-[#D4A949] text-xs uppercase tracking-[0.3em] font-semibold mb-4">03 — Verwandlung</p>
              <h2 style={ARCHIVO} className="text-5xl lg:text-7xl leading-[0.95] text-[#F4F0E8] uppercase tracking-[-0.02em]">
                Vorher / <span className="text-[#D4A949]">Nachher</span>
              </h2>
            </div>
            <p className="text-[#A8A39A] max-w-md text-lg">Bewegen Sie den Regler, um die Verwandlung zu sehen.</p>
          </div>
          <div className="rounded-none overflow-hidden border border-[#2A2A2A]">
            <BeforeAfterSlider beforeSrc={baList[0].beforeSrc} afterSrc={baList[0].afterSrc} />
          </div>
        </div>
      </section>

      {/* STIMMEN / REVIEWS */}
      <section id="stimmen" className="py-24 lg:py-32 bg-[#0F0F0F]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <p className="text-[#D4A949] text-xs uppercase tracking-[0.3em] font-semibold mb-4">04 — Stimmen</p>
              <h2 style={ARCHIVO} className="text-5xl lg:text-7xl leading-[0.95] text-[#F4F0E8] uppercase tracking-[-0.02em]">
                Was Kunden<br /><span className="text-[#D4A949]">sagen</span>
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-10">
              <div className="flex items-center gap-4">
                <div className="flex">{[1,2,3,4,5].map(i => <Star key={i} className="w-6 h-6 fill-[#D4A949] text-[#D4A949]" />)}</div>
                <p style={ARCHIVO} className="text-3xl text-[#F4F0E8]">{ratingText}</p>
                <p className="text-sm text-[#A8A39A] uppercase tracking-wider">aus {totalCount}+ Bewertungen</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-1 items-start">
            {visibleReviews.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-[#161616] p-10 border border-[#2A2A2A] hover:border-[#D4A949] transition-colors flex flex-col"
              >
                <div className="flex mb-6">{Array.from({length: r.rating || 5}).map((_, idx) => <Star key={idx} className="w-3.5 h-3.5 fill-[#D4A949] text-[#D4A949]" />)}</div>
                <p className="text-[#D7D2C8] text-lg leading-relaxed mb-8 flex-1">
                  <ReviewText
                    text={r.text}
                    threshold={260}
                    buttonClassName="text-sm text-[#D4A949] hover:text-[#E5C063] font-semibold uppercase tracking-wider"
                  />
                </p>
                <div className="pt-6 border-t border-[#2A2A2A] mt-auto">
                  <p style={ARCHIVO} className="text-[#F4F0E8] text-lg uppercase">{r.author}</p>
                  {r.date && <p className="text-xs text-[#A8A39A] uppercase tracking-wider mt-1">{r.date}</p>}
                </div>
              </motion.div>
            ))}
          </div>
          {reviewList.length > 6 && (
            <div className="text-center mt-12">
              <button
                type="button"
                onClick={() => setShowAllReviews(!showAllReviews)}
                data-testid="reviews-toggle"
                className="px-8 py-3.5 border border-[#D4A949] text-[#D4A949] hover:bg-[#D4A949] hover:text-[#0A0A0A] transition-colors uppercase tracking-wider text-sm font-semibold"
              >
                {showAllReviews ? "Weniger anzeigen" : `Alle ${reviewList.length} Bewertungen anzeigen`}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* PARTNER STRIP */}
      <section className="py-12 bg-[#0A0A0A] border-y border-[#2A2A2A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-wrap items-center justify-center gap-10 lg:gap-16">
          {["CAPAROL", "BRILLUX", "STO", "ALPINA", "HERBOL", "KEIM"].map((p) => (
            <span key={p} style={ARCHIVO} className="text-[#5A554A] text-xl tracking-[0.2em] uppercase">{p}</span>
          ))}
        </div>
      </section>

      {/* KONTAKT */}
      <section id="kontakt" className="py-24 lg:py-32 bg-[#0F0F0F]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="text-[#D4A949] text-xs uppercase tracking-[0.3em] font-semibold mb-4">05 — Kontakt</p>
            <h2 style={ARCHIVO} className="text-5xl lg:text-7xl leading-[0.95] text-[#F4F0E8] uppercase tracking-[-0.02em] mb-10">
              Starten<br /><span className="text-[#D4A949]">wir.</span>
            </h2>
            <ul className="space-y-6 mb-10">
              <li className="flex gap-4 items-start">
                <Phone className="w-5 h-5 text-[#D4A949] flex-shrink-0 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#A8A39A] mb-1">Telefon</p>
                  <a href={`tel:${siteInfo?.phone}`} className="text-[#F4F0E8] text-lg hover:text-[#D4A949] transition-colors">{siteInfo?.phone}</a>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <Mail className="w-5 h-5 text-[#D4A949] flex-shrink-0 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#A8A39A] mb-1">E-Mail</p>
                  <a href={`mailto:${siteInfo?.email}`} className="text-[#F4F0E8] text-lg hover:text-[#D4A949] transition-colors">{siteInfo?.email}</a>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <MapPin className="w-5 h-5 text-[#D4A949] flex-shrink-0 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#A8A39A] mb-1">Adresse</p>
                  <p className="text-[#F4F0E8] text-lg">{siteInfo?.address}</p>
                </div>
              </li>
            </ul>
          </div>

          <form onSubmit={handleContact} className="lg:col-span-7" data-testid="bold-contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Input required placeholder="Ihr Name *" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-[#161616] border-[#2A2A2A] text-[#F4F0E8] h-14 placeholder:text-[#5A554A]" />
              <Input required type="email" placeholder="E-Mail *" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-[#161616] border-[#2A2A2A] text-[#F4F0E8] h-14 placeholder:text-[#5A554A]" />
              <Input placeholder="Telefon" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-[#161616] border-[#2A2A2A] text-[#F4F0E8] h-14 placeholder:text-[#5A554A]" />
              <Input placeholder="Betreff" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} className="bg-[#161616] border-[#2A2A2A] text-[#F4F0E8] h-14 placeholder:text-[#5A554A]" />
            </div>
            <Textarea required rows={7} placeholder="Ihre Nachricht *" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} className="mt-5 bg-[#161616] border-[#2A2A2A] text-[#F4F0E8] placeholder:text-[#5A554A]" />
            <label className="mt-5 flex items-start gap-3 text-sm text-[#A8A39A] cursor-pointer" data-testid="bold-privacy-consent">
              <input type="checkbox" required data-testid="bold-privacy-checkbox" className="mt-1 h-4 w-4 accent-[#D4A949]" />
              <span>Ich habe die <Link to="/datenschutz" className="text-[#D4A949] hover:underline">Datenschutzerklärung</Link> gelesen und stimme der Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage zu. *</span>
            </label>
            <button type="submit" disabled={sending} className="mt-8 inline-flex items-center gap-3 px-10 py-4 bg-[#D4A949] text-[#0A0A0A] uppercase tracking-wider font-semibold hover:bg-[#E5C063] transition-colors">
              {sending ? "Wird gesendet..." : <>Anfrage senden <ArrowUpRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#060606] border-t border-[#2A2A2A] py-12">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            {siteInfo?.logo_path && <img src={fileUrl(siteInfo.logo_path)} alt="" className="h-12 w-auto object-contain" />}
            <div>
              <p style={ARCHIVO} className="text-xl text-[#F4F0E8] uppercase tracking-tight">{siteInfo?.company_name}</p>
              <p className="text-sm text-[#A8A39A]">{siteInfo?.tagline}</p>
            </div>
          </div>
          <div className="flex gap-6 text-sm text-[#A8A39A]">
            <p>© {new Date().getFullYear()} {siteInfo?.company_name}</p>
            <Link to="/impressum" data-testid="footer-impressum-link" className="hover:text-[#D4A949] transition-colors">Impressum</Link>
            <Link to="/datenschutz" data-testid="footer-datenschutz-link" className="hover:text-[#D4A949] transition-colors">Datenschutz</Link>
            <Link to="/admin/login" className="hover:text-[#D4A949] transition-colors">Admin</Link>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-6 pt-6 border-t border-[#2A2A2A] text-center lg:text-right">
          <a
            href="https://www.kauzwerk.de"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-credit"
            className="text-xs text-[#5A554A] hover:text-[#D4A949] transition-colors uppercase tracking-[0.2em]"
          >
            made by <span style={ARCHIVO} className="text-[#A8A39A]">KAUZWERK</span> · www.kauzwerk.de
          </a>
        </div>
      </footer>
    </div>
  );
}
